import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { Subject, Subscription, debounceTime, takeUntil, tap } from 'rxjs';
import { PageLoaderService } from '../../../core/service/page-loader.service';
import { DialogComponent } from '../../../shared/component/dynamic-form/dialog-box/dialog-component';
import {
  CombinedState,
  DryerDTO,
  Field,
  FormValues,
  InputFields,
  ModelDTO,
  PayloadDto,
  ProductEntity,
  SelectedValue,
  TransformData,
} from '../../../shared/models';
import { NotificationService } from '../../../shared/services/notification.service';
import { ProductsService } from '../../../shared/services/products.service';

@Component({
  selector: 'app-product-content',
  templateUrl: './product-content.component.html',
  styleUrls: ['./product-content.component.scss'],
  standalone: false,
})
export class ProductContentComponent implements OnInit, AfterViewInit, OnDestroy {
  selectedValues: { grandparent: string; label: string; children: Array<{ dryercategory?: string } | string> | null }[] = [];
  typesOfDryers: string[] = [];
  subcategory: string = '';
  typeKeys: string[] = [];
  dryerSubCategory: string = '';
  categoryKeys: string[] = [];
  activeTab: string = 'condition';
  fields: Field[] = [];
  public form!: FormGroup;
  dryerTypeLabel: string = '';
  allRequiredFieldsPresent: boolean = false;
  unsubcribe: Subscription | undefined;
  private dataSubscription?: Subscription;
  transformData: TransformData = {};
  queryParameters: string[] = [];
  dryerType: string = '';
  dryerCategory: string = '';
  exactModelDryerType: string = '';
  exactModelcategoryType: string = '';
  finalCategory: string[] = [];
  public fetchedData: ModelDTO[] = [];
  public specificModelData: ModelDTO[] = [];
  models: string[] = [];
  modelNames: string = '';
  defaultModelName: string = '';
  payloadData: { dryerGroup: string; dryerType: string; dryerCategory: string | null }[] = [];
  sensorData: { sensorValues: { [key: string]: number }; dryers: DryerDTO[] } = {
    sensorValues: {},
    dryers: [],
  };
  dryerGroup: string;
  allRequiredFieldsHaveValue: boolean = false;
  isLoading: boolean = true;
  hasSelection: boolean = false;
  checkboxesLoaded: boolean = false;
  private checkboxesLoadedSub?: Subscription;
  fetchLatestFormValues: CombinedState;
  private formValuesReady = false;
  private viewInitialized = false;
  private isFirstSubmit = true;
  private selectionChange$ = new Subject<void>();

  constructor(
    private productservice: ProductsService,
    private pageLoaderService: PageLoaderService,
    private dialog: MatDialog,
    private notificationService: NotificationService,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    this.unsubcribe = this.productservice.selectedValues$
      .pipe(
        tap((value: SelectedValue[]) => {
          if (value && value.length > 0) {
            this.isLoading = true;
            this.hasSelection = true;
          } else {
            this.isLoading = false;
            this.hasSelection = false;
            this.fields = [];
          }
          // Cancel any in-flight API requests from the previous selection
          // so a stale 401 response cannot trigger logout after the user changes selection.
          this.selectionChange$.next();
        }),
        debounceTime(300),
      )
      .subscribe((value: SelectedValue[]) => {
        this.updateSelectedValues(value);
      });

    this.checkboxesLoadedSub = this.productservice.checkboxesLoaded$.subscribe((loaded) => {
      this.checkboxesLoaded = loaded;
    });

    // Set up data subscription immediately so results are captured even before auto-submit fires.
    this.subscribeToData();

    if (this.fetchLatestFormValues) {
      this.selectTab(this.activeTab);
      this.handleSubmit();
    }
  }

  ngAfterViewInit() {
    this.viewInitialized = true;
    this.checkAndProcessFormValues();
  }

  ngOnDestroy(): void {
    this.unsubcribe?.unsubscribe();
    this.dataSubscription?.unsubscribe();
    this.checkboxesLoadedSub?.unsubscribe();
    this.selectionChange$.complete();
  }

  private checkAndProcessFormValues() {
    if (this.viewInitialized && this.formValuesReady && !this.isLoading && this.fetchLatestFormValues && this.isFirstSubmit) {
      this.isFirstSubmit = false;
      setTimeout(() => {
        this.selectTab(this.activeTab);
        this.handleSubmit();
      });
    }
  }

  updateSelectedValues(value: SelectedValue[]) {
    this.selectedValues = value;
    this.payloadData = [];
    this.sensorData = { sensorValues: {}, dryers: [] };

    this.selectedValues.forEach((item: SelectedValue) => {
      const dryerType = item?.label;
      const dryerGroup = item?.grandparent;

      if (item.children) {
        item.children.forEach((dryercategory: any) => {
          const dryerCategory = this.extractDryerCategory(dryercategory);
          this.payloadData.push({ dryerGroup, dryerType, dryerCategory });
          this.sensorData.dryers.push({ dryerGroup, dryerType, dryerCategory });
        });
      } else {
        const dryerCategory = null;
        this.payloadData.push({ dryerGroup, dryerType, dryerCategory });
        this.sensorData.dryers.push({ dryerGroup, dryerType, dryerCategory });
      }
    });

    if (this.payloadData.length === 0) {
      this.hasSelection = false;
      this.isLoading = false;
      this.fields = [];
      this.allRequiredFieldsPresent = false;
      this.productservice.clearProcessedConditionResults();
      this.productservice.updateFetchedData([]);
      return;
    }

    this.hasSelection = true;
    this.isLoading = true;
    // Clear stale data only after isLoading=true so the @if hides the form
    // before DryerFilterTable's subscriber runs — prevents NG0100.
    this.productservice.clearProcessedConditionResults();
    this.productservice.updateFetchedData([]);
    this.productservice
      .getProductFields(this.payloadData)
      .pipe(takeUntil(this.selectionChange$))
      .subscribe(
        (fields) => {
          this.isLoading = false;
          this.fields = [...(Object.values(JSON.parse(JSON.stringify(fields))) as Field[])];
          // Defer CD past the current check cycle to avoid NG0100.
          Promise.resolve().then(() => {
            this.cdr.detectChanges();
            this.checkAndProcessFormValues();
          });
        },
        (_error) => {
          this.isLoading = false;
          Promise.resolve().then(() => this.cdr.detectChanges());
        },
      );
    if (this.sensorData.dryers.length > 0) {
      this.productservice
        .getModelNames(this.sensorData.dryers)
        .pipe(takeUntil(this.selectionChange$))
        .subscribe(
          (data) => {
            // Defer past the current check cycle: assigning models synchronously here can change
            // the [disabled] binding (it reads models.includes(...)) after it was checked → NG0100.
            Promise.resolve().then(() => {
              this.models = data;
              this.cdr.detectChanges();
            });
          },
          (_error) => {
            Promise.resolve().then(() => {
              this.models = [];
              this.cdr.detectChanges();
            });
          },
        );
    }
  }

  transformPropertyName(name: string): string {
    return InputFields[name as keyof typeof InputFields];
  }

  fetchExactModel(queryParams: PayloadDto) {
    this.pageLoaderService.showSpinnerForDataLoading(
      this.productservice
        .getExactModels(queryParams)
        .pipe(takeUntil(this.selectionChange$))
        .subscribe(
          (res: ModelDTO[]) => {
            this.fetchedData = res;
            this.fetchedData = res.map((model) => ({
              ...model,
              calculatedId: model.calculatedId || 0, // default value if missing
              pressureDrop: String(model.pressureDrop || 0), // ensure pressureDrop is set
              exactModel: model.exactModel as ProductEntity, // set default values as needed
              airFlowMargin: model.airFlowMargin || 0,
              outletAirFlow: model.outletAirFlow || 0,
            }));
            this.productservice.updateFetchedData(this.fetchedData);
          },
          (_error) => {
            this.productservice.updateFetchedData([]);
          },
        ),
    );
  }

  fetchSpecificModel(transformData: FormValues, specificModel: string) {
    this.pageLoaderService.showSpinnerForDataLoading(
      this.productservice
        .getSpecificModel(transformData, specificModel)
        .pipe(takeUntil(this.selectionChange$))
        .subscribe(
          (res: ModelDTO[]) => {
            this.specificModelData = res.map((model) => ({
              ...model,
              matchPercentage: model.matchPercentage || 0,
              calculatedId: model.calculatedId || 0,
              pressureDrop: String(model.pressureDrop || 0),
              exactModel: model.exactModel as ProductEntity,
            }));
            // Update the data in the service
            this.productservice.updateModelData(this.specificModelData);
          },
          (_error) => {
            this.productservice.updateModelData([]);
          },
        ),
    );
  }

  handleSubmit() {
    this.clearFetchedData();

    if (!this?.fetchLatestFormValues) {
      this.showDialog('MESSAGES.FORMVALUES_NOT_DEFINED');
      return;
    }

    const { formValues } = this.fetchLatestFormValues;
    this.validateDryers(formValues);

    if (!this.allRequiredFieldsPresent) {
      this.allRequiredFieldsHaveValue = false;
      this.clearFetchedData();
      return;
    }

    this.processFormValues(formValues);
    this.updateServices();

    this.fetchModels(formValues);
  }

  private showDialog(messageKey: string, params?: any): void {
    this.dialog.open(DialogComponent, {
      data: { message: this.translate.instant(messageKey, params) },
    });
  }

  private validateDryers(formValues: any): void {
    const minInlet = formValues['minInletTemperature'];
    const maxInlet = formValues['inletTemperature'];

    this.selectedValues.forEach((dryer) => {
      this.dryerGroup = dryer.grandparent;
      this.dryerType = dryer.label;

      if (this.dryerGroup === 'Subfreezing Dryers' && this.isInvalidInlet(minInlet, maxInlet)) {
        this.showDialog('MESSAGES.MININLET_EXCEEDS_MAXINLET_SUBFREEZING', { value1: minInlet, value2: maxInlet });
      }
    });
  }

  private isInvalidInlet(minInlet: number, maxInlet: number): boolean {
    if (minInlet === null || maxInlet === null) {
      return false;
    }
    return minInlet > maxInlet;
  }

  private processFormValues(formValues: any): void {
    const newTransformData: { [key: string]: number | string | null } = {};
    const keysToExclude = ['selectedModel'];

    Object.keys(formValues)
      .filter((key) => !keysToExclude.includes(key))
      .forEach((key) => {
        const name = this.transformPropertyName(key);
        newTransformData[name] = formValues[key];
      });

    this.allRequiredFieldsHaveValue = true;
    this.transformData = newTransformData;
    this.sensorData.sensorValues = this.transformData as { [key: string]: number };
  }

  private updateServices(): void {
    this.productservice.updateSensorValues({ sensorValues: this.transformData });
    this.productservice.clearSelectedCheckboxes();
    this.productservice.setFilteredSelectedRows([]);
  }

  private fetchModels(formValues: any): void {
    if (this.activeTab === 'model' && formValues['selectedModel']) {
      this.fetchSpecificModel(this.transformData, formValues['selectedModel']);
    } else if (this.activeTab === 'condition') {
      this.fetchExactModel(this.sensorData);
    }
  }
  selectTab(tab: string) {
    this.activeTab = tab;
    this.allRequiredFieldsPresent = false;
    if (this.sensorData?.dryers?.length > 0 && this.activeTab === 'model') {
      this.productservice
        .getModelNames(this.sensorData?.dryers)
        .pipe(takeUntil(this.selectionChange$))
        .subscribe(
          (data) => {
            // Defer past the current check cycle: assigning models synchronously here can change
            // the [models] / [disabled] bindings after they were checked → NG0100.
            Promise.resolve().then(() => {
              this.models = data;
              this.modelNames = '';
              this.defaultModelName = '';
              this.cdr.detectChanges();
            });
          },
          (_error) => {
            Promise.resolve().then(() => {
              this.models = [];
              this.modelNames = '';
              this.defaultModelName = '';
              this.cdr.detectChanges();
            });
          },
        );
    }

    this.clearDataForInactiveTabs();
    this.subscribeToData();

    // Re-evaluate from the last form emission. selectTab() resets allRequiredFieldsPresent, but an
    // already-initialised condition form does not re-emit, so without this the Submit button stays
    // disabled (and the auto-submit early-returns) when returning to a tab whose form is valid.
    if (this.activeTab === 'condition') {
      this.allRequiredFieldsPresent = this.evaluateRequiredFieldsPresent();
    }
  }

  private evaluateRequiredFieldsPresent(): boolean {
    const state = this.fetchLatestFormValues;
    if (!state?.requiredFields?.length) {
      return false;
    }
    const { formValues, requiredFields } = state;
    if (this.activeTab === 'model' && formValues['selectedModel'] === '') {
      return false;
    }
    return requiredFields.every(
      (field) =>
        formValues[field.name] !== undefined &&
        formValues[field.name] !== null &&
        formValues[field.name] !== '' &&
        !Number.isNaN(formValues[field.name]),
    );
  }

  onModelSelected(selectedModel: string) {
    this.modelNames = selectedModel;
  }

  onDefaulModelName(data: string) {
    this.defaultModelName = data;
  }

  handleFormValueChanged(data: CombinedState) {
    const { formValues, requiredFields } = data;
    this.fetchLatestFormValues = data;
    this.formValuesReady = true;

    Promise.resolve().then(() => {
      this.allRequiredFieldsPresent = false;

      if (this.activeTab === 'model' && formValues['selectedModel'] && !this.models.includes(formValues['selectedModel'])) {
        formValues['selectedModel'] = '';
      }

      if (this.activeTab === 'model' && formValues['selectedModel'] === '') {
        this.allRequiredFieldsPresent = false;
      } else {
        this.allRequiredFieldsPresent = requiredFields.every((field) => {
          return (
            formValues[field.name] !== undefined &&
            formValues[field.name] !== null &&
            formValues[field.name] !== '' &&
            !Number.isNaN(formValues[field.name])
          );
        });
        this.checkAndProcessFormValues();
      }
      this.cdr.detectChanges();
    });
  }
  onRequiredFieldsChange(present: boolean): void {
    this.allRequiredFieldsHaveValue = present;
    if (!present) {
      this.clearDataForInactiveTabs();
      this.clearFetchedData();
    }
  }

  clearDataForInactiveTabs(): void {
    if (this.activeTab === 'condition') {
      this.specificModelData = [];
    } else if (this.activeTab === 'model') {
      this.fetchedData = [];
    }
  }

  clearFetchedData(): void {
    this.productservice.updateFetchedData([]);
    this.productservice.updateModelData([]);
    this.productservice.clearProcessedConditionResults();
  }

  subscribeToData(): void {
    this.dataSubscription?.unsubscribe();

    if (this.activeTab === 'condition') {
      this.dataSubscription = this.productservice.fetchedData$.subscribe((data) => {
        this.fetchedData = data;
      });
    } else if (this.activeTab === 'model') {
      this.dataSubscription = this.productservice.modelData$.subscribe((data) => {
        this.specificModelData = data;
      });
    }
  }

  private extractDryerCategory(rawCategory: { dryercategory?: string } | string | null | undefined): string | null {
    if (typeof rawCategory === 'string') {
      return rawCategory;
    }
    return rawCategory?.dryercategory ?? null;
  }
}
